/**
 * Drift correction (PRD §5). Decides, each loop tick, how a player should chase
 * the authoritative target without jarring the viewer:
 *
 *   |delta| > hardSeekSec     → hard seek            (join, big jump)
 *   |delta| > slewDeadbandSec → imperceptible ±rate nudge (slew)
 *   else                      → run at nominal 1.0×
 *
 * `delta = target - current`. Positive delta ⇒ player is behind ⇒ speed up.
 */
export interface DriftConfig {
  /** Hard-seek beyond this error (a join / host seek / recovery from a long stall). */
  hardSeekSec: number;
  /** Within this, play untouched at 1.0× (the no-correction tolerance band). */
  slewDeadbandSec: number;
  /** Max fractional rate deviation, e.g. 0.03 ⇒ playbackRate ∈ [0.97, 1.03]. */
  maxRateDelta: number;
  /** Proportional gain mapping seconds-of-error → rate deviation. */
  gainK: number;
  /** Minimum time between hard seeks, so a seek-into-a-stall can't thrash. */
  seekCooldownMs: number;
}

export const DEFAULT_DRIFT: DriftConfig = {
  // Only seek for real discontinuities; moderate drift is slewed, not skipped.
  hardSeekSec: 2.5,
  // Generous tolerance: within ¾s, leave playback alone (no audible nudging).
  slewDeadbandSec: 0.75,
  // Gentle, pitch-preserved correction when it does slew.
  maxRateDelta: 0.03,
  gainK: 0.5,
  // After a hard seek, wait this long before another — lets buffering settle.
  seekCooldownMs: 4000,
};

export type DriftDecision =
  | { kind: "seek"; seekToSec: number }
  | { kind: "slew"; playbackRate: number }
  | { kind: "nominal"; playbackRate: 1 };

export function clamp(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x));
}

export function driftDecision(
  targetSec: number,
  currentSec: number,
  cfg: DriftConfig = DEFAULT_DRIFT,
): DriftDecision {
  const delta = targetSec - currentSec;
  const abs = Math.abs(delta);

  if (abs > cfg.hardSeekSec) {
    return { kind: "seek", seekToSec: targetSec };
  }
  if (abs > cfg.slewDeadbandSec) {
    const rate = 1 + clamp(delta * cfg.gainK, -cfg.maxRateDelta, cfg.maxRateDelta);
    return { kind: "slew", playbackRate: rate };
  }
  return { kind: "nominal", playbackRate: 1 };
}
